import React, { useEffect, useRef } from "react";
import { IAgeSelectMenu, ISelectMenuItemData } from "../../interface";
import SearchableSelectMenu from "../SearchableSelectMenu";

const AgeSelectMenu: React.FC<IAgeSelectMenu> = ({
  dayRegister,
  dayFieldError,
  monthFieldError,
  yearFieldError,
  defaultValues,
  monthRegister,
  yearRegister,
  ...props
}) => {
  const lastCommittedYearRef = useRef(defaultValues?.year ?? "");
  const lastCommittedMonthRef = useRef(defaultValues?.month ?? "");
  const lastCommittedDayRef = useRef(defaultValues?.day ?? "");
  const typedYearRef = useRef("");
  const typedMonthRef = useRef("");
  const typedDayRef = useRef("");

  useEffect(() => {
    lastCommittedYearRef.current = defaultValues?.year ?? "";
    lastCommittedMonthRef.current = defaultValues?.month ?? "";
    lastCommittedDayRef.current = defaultValues?.day ?? "";
  }, [defaultValues?.year, defaultValues?.month, defaultValues?.day]);

  const years: ISelectMenuItemData[] = [];
  for (let i = 0; i <= 20; i++) {
    years.push({ title: `${i}`, id: `${i} Years` });
  }

  // const currentMonth = new Date().getMonth();
  const months: ISelectMenuItemData[] = [];

  for (let i = 0; i <= 12; i++) {
    months.push({ title: `${i}`, id: `${i} Months` });
  }

  const days: ISelectMenuItemData[] = [];

  for (let i = 0; i <= 31; i++) {
    days.push({ title: `${i}`, id: `${i} Days` });
  }

  const handleSelectYear = (item: ISelectMenuItemData | null) => {
    if (props?.onYearSelect) {
      const y = item ? item.title : "";
      typedYearRef.current = y;
      lastCommittedYearRef.current = y;
      props?.onYearSelect(y);
    }
  };

  const handleSelectMonth = (item: ISelectMenuItemData | null) => {
    if (props?.onMonthSelect) {
      const m = item ? item.title : "";
      typedMonthRef.current = m;
      lastCommittedMonthRef.current = m;
      props.onMonthSelect(m);
    }
  };

  const handleSelectDay = (item: ISelectMenuItemData | null) => {
    if (props?.onDaySelect) {
      const d = item ? item.title : "";
      typedDayRef.current = d;
      lastCommittedDayRef.current = d;
      props.onDaySelect(d);
    }
  };

  const toValidNonNegativeNumericString = (value: string): string => {
    const trimmed = value.trim();
    if (trimmed === "") return "";
    if (!/^\d+$/.test(trimmed)) return "";
    return trimmed;
  };

  const handleYearInputBlur = (inputValue: string) => {
    const value = toValidNonNegativeNumericString(typedYearRef.current || inputValue || "");
    if (value === "" || value === lastCommittedYearRef.current) return;
    typedYearRef.current = value;
    lastCommittedYearRef.current = value;
    if (props?.onYearInputBlur) {
      props.onYearInputBlur(value);
    }
  };

  const handleMonthInputBlur = (inputValue: string) => {
    const value = toValidNonNegativeNumericString(typedMonthRef.current || inputValue || "");
    if (value === "" || value === lastCommittedMonthRef.current) return;
    typedMonthRef.current = value;
    lastCommittedMonthRef.current = value;
    if (props?.onMonthInputBlur) {
      props.onMonthInputBlur(value);
    }
  };

  const handleDayInputBlur = (inputValue: string) => {
    const value = toValidNonNegativeNumericString(typedDayRef.current || inputValue || "");
    if (value === "" || value === lastCommittedDayRef.current) return;
    typedDayRef.current = value;
    lastCommittedDayRef.current = value;
    if (props?.onDayInputBlur) {
      props.onDayInputBlur(value);
    }
  };

  return (
    <div className="flex justify-between w-full ">
      <div className="col-span-1">
        <div className="w-full flex justify-between items-center">
          <SearchableSelectMenu
            disabled={props?.disabled && props.disabled === true ? true : false}
            className="text-xs w-full"
            onSelectItem={handleSelectYear}
            containerClassName=""
            register={yearRegister}
            fieldError={yearFieldError}
            allowCustomInput
            onMenuInputBlur={handleYearInputBlur}
            onInputChange={(value) => {
              typedYearRef.current = value;
              handleYearInputBlur(value);
            }}
            defaultSelected={
              years.filter((x) => x.title === defaultValues?.year)[0] ??
              (defaultValues?.year ? { title: defaultValues.year, id: `${defaultValues.year} Years` } : undefined)
            }
            errorMessages={[]}
            placeholder={"Y"}
            selectItems={years}
          />
          <span className="p-2 text-gray-500">Yr</span>
        </div>
      </div>
      <div className="col-span-1 ml-2">
        <div className="w-full flex justify-between items-center">
          <SearchableSelectMenu
            disabled={props?.disabled && props.disabled === true ? true : false}
            className="text-xs w-full"
            onSelectItem={handleSelectMonth}
            containerClassName=""
            allowCustomInput
            onMenuInputBlur={handleMonthInputBlur}
            onInputChange={(value) => {
              typedMonthRef.current = value;
              handleMonthInputBlur(value);
            }}
            defaultSelected={
              months.filter((x) => x.title === defaultValues?.month)[0] ??
              (defaultValues?.month ? { title: defaultValues.month, id: `${defaultValues.month} Months` } : undefined)
            }
            register={monthRegister}
            fieldError={monthFieldError}
            errorMessages={[]}
            placeholder={"M"}
            selectItems={months}
          />
          <span className="p-2 text-gray-500">M</span>
        </div>
      </div>
      <div className="col-span-1 ml-2">
        <div className="w-full flex justify-between items-center">
          <SearchableSelectMenu
            disabled={props?.disabled && props.disabled === true ? true : false}
            className="text-xs w-full"
            onSelectItem={handleSelectDay}
            register={dayRegister}
            containerClassName=""
            allowCustomInput
            onMenuInputBlur={handleDayInputBlur}
            onInputChange={(value) => {
              typedDayRef.current = value;
              handleDayInputBlur(value);
            }}
            defaultSelected={
              days.filter((x) => x.title === defaultValues?.day)[0] ??
              (defaultValues?.day ? { title: defaultValues.day, id: `${defaultValues.day} Days` } : undefined)
            }
            fieldError={dayFieldError}
            placeholder={"D"}
            errorMessages={[]}
            selectItems={days}
          />
          <span className="p-2 text-gray-500">D</span>
        </div>
      </div>
    </div>
  );
};

export default AgeSelectMenu;
